/**
 * Card Utilities
 *
 * Shared card value calculations for game logic and validation.
 * Bavarian deck uses ranks 6-K (values 1-8) and Ace (value 9, highest).
 */

/**
 * Get the game value of a card for comparison.
 * @param {string} cardId - Card identifier (e.g., 'h6', 's13', 'd1')
 * @returns {number} Card value (1-9, where 9 is Ace/highest)
 */
export function getCardValue(cardId) {
  const rank = parseInt(cardId.substring(1), 10);
  if (rank === 1) return 9;  // Ace is highest
  if (rank >= 6 && rank <= 13) return rank - 5;  // 6->1, 7->2, ..., K->8
  return 0;  // Invalid card for Bavarian deck
}

/**
 * Get cumulative value of attack cards on a defender or general.
 * @param {string[]} attacks - Array of card IDs attacking the target
 * @returns {number} Total attack value
 */
export function getCumulativeValue(attacks) {
  return attacks.reduce((sum, cardId) => sum + getCardValue(cardId), 0);
}

/**
 * Check if an attack exceeds the defender/general value.
 * @param {string} targetCard - The defender or general card ID
 * @param {string[]} attacks - Array of attacking card IDs
 * @returns {boolean} True if cumulative attack value > target value
 */
export function attackExceedsTarget(targetCard, attacks) {
  const targetValue = getCardValue(targetCard);
  const attackValue = getCumulativeValue(attacks);
  return attackValue > targetValue;
}

/**
 * Check if a card is an Ace.
 * @param {string} cardId - Card identifier
 * @returns {boolean} True if card is an Ace
 */
export function isAce(cardId) {
  const rank = parseInt(cardId.substring(1), 10);
  return rank === 1;
}

/**
 * Check if player's hand contains an Ace.
 * @param {string[]} hand - Array of card IDs in hand
 * @returns {boolean} True if hand contains at least one Ace
 */
export function handHasAce(hand) {
  return hand.some(isAce);
}
